import { Input, Component, OnChanges, AfterViewInit, forwardRef, ViewChild } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NgModel } from '@angular/forms';

import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { RecordingPolicy } from 'rev-shared/media/RecordingPolicy';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';

import { ISearchLoader } from 'rev-portal/search/Contract';
import { InsightSearchHelper } from 'rev-portal/search/InsightSearchHelper.Service';
import { VideoSearchService } from 'rev-portal/search/VideoSearch.Service';

import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastModel } from './EditWebcastModel';
import { EditWebcastService } from './EditWebcast.Service';
import { WebcastStatus } from '../webcast/WebcastStatus';

@Component({
	selector: 'edit-webcast-recording-settings',
	templateUrl: './EditWebcastRecordingSettings.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditWebcastRecordingSettingsComponent),
		multi: true,
	}],
	standalone: false
})
export class EditWebcastRecordingSettingsComponent extends EditWebcastBaseSettingsComponent implements OnChanges, AfterViewInit {
	@Input() public features: IMediaFeatures;
	@Input() public webcast: EditWebcastModel;
	@Input() public isSidebar: boolean;

	@ViewChild('preRollVideo', { static: true, read: NgModel }) public preRollVideo: NgModel;
	@ViewChild('postRollVideo', { static: true, read: NgModel }) public postRollVideo: NgModel;

	private readonly accountId: string = this.UserContext.getAccount().id;
	private readonly baseLoader = this.VideoSearchService.getVideoSearchLoader(this.accountId);
	public readonly videoSearchLoader: ISearchLoader = (query: string, opts: any) => {
		const hasDual = this.webcast.isVciSource;
		return this.baseLoader(query, { ...opts, hasDual });
	};

	private accessControlInsightOptions: any;

	public hideAutomaticRecordingOption: boolean;

	constructor(
		private readonly EditWebcastService: EditWebcastService,
		private readonly InsightSearchHelper: InsightSearchHelper,
		private readonly UserContext: UserContextService,
		private readonly VideoSearchService: VideoSearchService
	) {
		super();
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		if(!this.webcast.recordingUploaderUserId) {
			window.setTimeout(() => {
				const user: AbstractControl = this.form.control.get('uploaderUser');
				if(user) {
					user.markAsDirty();
				}
			}, 500);
		}
		this.validateVideoBumpers();
	}

	public ngOnChanges(): void {
		if (this.webcast && !this.accessControlInsightOptions) {
			this.initInsightOptions();
		}

		this.hideAutomaticRecordingOption = this.features?.recordingPolicy === RecordingPolicy.REQUIRE;
	}

	public initInsightOptions(): void {
		const { accountId, webcast } = this;

		this.accessControlInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId,
			users: true,
			groups: false,
			loadQueryPage: query => this.EditWebcastService.queryMediaContributors(query),
			getEntityIds: () => webcast.recordingUploaderUserId ? [webcast.recordingUploaderUserId] : []
		});
	}

	public validateVideoBumpers(): void {

		this.validatePreRoll();
		this.validatePostRoll();
	}

	public validatePostRoll(): void {
		toggleError(this.postRollVideo.control, 'videoSourceInvalid',
			this.webcast.postRollVideo && this.webcast.postRollVideo.hasDualStreams !== this.webcast.isVciSource);
	}

	public validatePreRoll(): void {
		toggleError(this.preRollVideo.control, 'videoSourceInvalid',
			this.webcast.preRollVideo && this.webcast.preRollVideo.hasDualStreams !== this.webcast.isVciSource);
	}

	public get isScheduled(): boolean {
		return !this.webcast.id ||
			this.webcast.status === WebcastStatus.Scheduled;
	}

	public get isNotRunning(): boolean {
		return !this.webcast.id ||
			[WebcastStatus.Completed, WebcastStatus.Deleted, WebcastStatus.Scheduled].includes(this.webcast.status);
	}
}
